import qs from 'qs';
import type { Brand } from './types';

/**
 * different brands have different encoding
 * ... requirements for login pages
 * the qs package's default behavior is
 * ... do not encode standard delimiters ("&", "=")
 * ... encode everything else
 * hotpads login adheres to qs standard
 * trulia requires that nothing is encoded
 * zillow requires special configuration
 * ... first query param must be "?url="
 * ... everything after this must be encoded
 * ... the first query param after url must be preceeded by an encoded "?"
 */

interface RouteInterface {
  brand: Brand;
  currentPathname: string;
  queryString: string;
  isInWebView?: boolean;
}

type BrandUrlMap = {
  [B in Brand]: string;
};

interface BuildUrlInterface {
  to: string;
  brand?: string;
  queryObject: { [key: string]: string };
}

function createUrlWithQueryObject(url: string, queryString?: string): string {
  if (!queryString) {
    return url;
  }

  return `${url}?${queryString}`;
}

function addZillowSigninParams(queryObject: { [key: string]: string }): { [key: string]: string } {
  const customizedQueryObject = {
    ...queryObject,
  };

  if (queryObject.utm_campaign === 'applynow' || queryObject.inviteId) {
    customizedQueryObject['he'] = 'Apply to this property';
    customizedQueryObject['sh'] = 'Please sign in or create an account to securely apply.';
  } else if (
    queryObject.url.match('rental-manager/properties/.+/applications') ||
    queryObject.url.match('renter-hub/applications')
  ) {
    customizedQueryObject['he'] = 'Continue to Applications';
    customizedQueryObject['sh'] = 'Please sign in to securely manage your rental applications.';
  } else if (
    queryObject.url.match('rental-manager/properties/.+/payments') ||
    queryObject.url.match('rental-manager/payments') ||
    queryObject.url.match('renter-hub/payments')
  ) {
    customizedQueryObject['he'] = 'Continue to Payments';
    customizedQueryObject['sh'] = 'Please sign in to securely manage your rental payments.';
  }

  return customizedQueryObject;
}

// this only should be called when the url is being built for the zillow app
function addZillowResetParams(queryObject: { [key: string]: string }): { [key: string]: string } {
  const customizedQueryObject = {
    ...queryObject,
  };

  customizedQueryObject.fromApp = 'true';

  return customizedQueryObject;
}

export function buildUrl({ brand = '', to, queryObject }: BuildUrlInterface): string {
  let queryString = '';

  if (brand === 'zillow') {
    queryString = `url=${encodeURIComponent(queryObject.url)}`;

    delete queryObject.url;

    if (Object.keys(queryObject).length) {
      const builtQueryString = Object.keys(queryObject).reduce((acc, key) => {
        let pre = '';

        if (!acc.length) {
          pre = encodeURIComponent('?');
        } else {
          pre = encodeURIComponent('&');
        }
        return (acc += `${pre}${encodeURIComponent(key + '=' + queryObject[key])}`);
      }, '');

      queryString += builtQueryString;
    }
  } else if (brand === 'hotpads') {
    queryString = qs.stringify(queryObject);
  } else if (brand === 'trulia') {
    queryString = qs.stringify(queryObject, { encode: false });
  }

  return createUrlWithQueryObject(to, queryString);
}

function matchBrandToUrl(brand: keyof BrandUrlMap, urls: BrandUrlMap) {
  return urls[brand];
}

export function buildQueryObject({
  brand,
  queryString,
  currentPathname,
}: {
  brand: Brand;
  queryString: string;
  currentPathname: string;
}): { [key: string]: string } {
  let params = {};

  switch (brand) {
    case 'zillow':
      params = {
        ...qs.parse(queryString, { ignoreQueryPrefix: true }),
        url: currentPathname,
      };
      break;
    case 'hotpads':
      params = {
        ...qs.parse(queryString, { ignoreQueryPrefix: true }),
        redirect: currentPathname,
      };
      break;
    case 'trulia':
      params = {
        ...qs.parse(queryString, { ignoreQueryPrefix: true }),
      };
      break;
  }

  return params;
}

export const routes = {
  signInPage: ({ brand, currentPathname, queryString = '', isInWebView }: RouteInterface): string => {
    const to = matchBrandToUrl(brand, {
      zillow: isInWebView ? '/renter-hub/nativeAppSignInPage' : '/user/acct/login/',
      hotpads: '/login',
      trulia: `/login/r${currentPathname}`,
    });

    let queryObject = buildQueryObject({ brand, currentPathname, queryString });

    if (!isInWebView && brand === 'zillow') {
      queryObject = addZillowSigninParams(queryObject);
    }

    return buildUrl({ brand, to, queryObject });
  },
  passwordResetPage: ({ brand, currentPathname, queryString = '', isInWebView }: RouteInterface): string => {
    const to = matchBrandToUrl(brand, {
      zillow: '/myzillow/ChangePassword.htm',
      hotpads: '/user/update-password',
      trulia: `/login/r${currentPathname}`,
    });

    let queryObject = buildQueryObject({ brand, currentPathname, queryString });

    if (isInWebView && brand === 'zillow') {
      queryObject = addZillowResetParams(queryObject);
    }

    return buildUrl({ brand, to, queryObject });
  },
};
